import React, { ReactNode } from 'react'
import classNames from 'classnames'

import { mq } from '../../utils'
import { BodyGap, IProps } from './index'
import { Text } from '../../elements/Text'
import { CTALink } from '../../elements/CTALink'
import { ColorIdTokenType, COLOR_TOKENS } from '../../utils/constants/color-tokens'

const getBodyGapValue = (bodyGapLabel: BodyGap) => {
  switch (bodyGapLabel) {
    case 'tiny':
      return '1rem'
    case 'small':
      return '1.5rem'
    case 'medium':
      return '2rem'
    case 'large':
      return '2.5rem'
    case 'huge':
    default:
      return '4rem'
  }
}

const getTextColorHex = (bgColor: ColorIdTokenType) => {
  switch (bgColor) {
    case 'slate':
    case 'blue':
    case 'violet':
      return COLOR_TOKENS.white.hex
    default:
      return COLOR_TOKENS.slate.hex
  }
}

export const GridContainer: React.FC<IProps> = ({
  layout = '3-3',
  titleSize = 'heading-2',
  eyebrow,
  title,
  introText,
  cta,
  ctaPosition = 'below-header',
  bodyGap = 'large',
  body,
  backgroundColor = 'transparent',
}) => {
  const bodyGapValue = getBodyGapValue(bodyGap as BodyGap)
  const textColorHex = getTextColorHex(backgroundColor)
  const backgroundColorHex = COLOR_TOKENS[backgroundColor as ColorIdTokenType].hex

  const classes = classNames('grid-container', {
    [`grid-container--${layout}`]: layout,
  })

  return (
    <>
      <div className={classes} data-testid="grid-container">
        {(eyebrow || title || introText) && (
          <div className="grid-container__header">
            {eyebrow && (
              <div className="grid-container__eyebrow">
                <Text brand="default" size="eyebrows">
                  {eyebrow}
                </Text>
              </div>
            )}
            {title && (
              <h2 className="grid-container__title">
                <Text brand="default" size={titleSize}>
                  {title}
                </Text>
              </h2>
            )}
            {introText && (
              <div className="grid-container__intro-text">
                <Text brand="default" size="heading-4">
                  {introText}
                </Text>
              </div>
            )}
          </div>
        )}

        {cta && ctaPosition === 'below-header' && (
          <div className="grid-container__cta">
            <CTALink {...cta} />
          </div>
        )}

        {body && (
          <div className="grid-container__body">
            {React.Children.map(
              body,
              (item: ReactNode) =>
                item &&
                React.isValidElement(item) && (
                  <li className="grid-container__item">{React.cloneElement(item)}</li>
                ),
            )}
          </div>
        )}

        {cta && ctaPosition === 'below-body' && (
          <div className="grid-container__cta">
            <CTALink {...cta} />
          </div>
        )}
      </div>

      <style jsx>
        {`
          .grid-container {
            display: grid;
            grid-template-columns: 1fr [content] 12fr 1fr;
            padding: 3.5rem 0;
            margin: 0 auto;
            color: ${textColorHex};
            background-color: ${backgroundColorHex};

            &__header,
            &__body,
            &__cta {
              grid-area: content;
              grid-row: span 1;
              max-width: $container-max-width;
              margin: 0 auto;
            }

            &__header {
              max-width: 64.25rem;
              text-align: center;
            }

            &__eyebrow {
              margin-bottom: 1.25rem;
            }

            * + &__intro-text {
              margin-top: 1.5rem;
            }

            &__cta {
              text-align: center;
            }

            &__header + &__cta {
              margin-top: 2.5rem;
            }

            &__header + &__body,
            &__cta + &__body {
              margin-top: 2.5rem;
            }

            &__body {
              display: grid;
              grid-gap: ${bodyGapValue};
            }

            &__body + &__cta {
              margin-top: 6.25rem;
            }

            &__item {
              // Remove additional padding from children.
              :global(& > section, & > div, & > section > div) {
                padding-top: 0;
                padding-bottom: 0;
              }
            }
          }

          @media (${mq.tablet}) {
            .grid-container {
              padding: 5rem 0;

              &__header + &__body,
              &__cta + &__body {
                margin-top: 4rem;
              }

              &--1-1,
              &--2-2,
              &--2-2-full-width,
              &--2-3,
              &--3-3 {
                .grid-container__body {
                  grid-template-columns: repeat(4, 1fr);
                }

                .grid-container__item {
                  grid-column: span 2;
                }
              }

              &--1-1 {
                .grid-container__item {
                  grid-column: 2/4;
                }
              }

              &--2-2,
              &--2-2-full-width,
              &--3-3 {
                .grid-container__item {
                  &:last-child:nth-child(2n - 1) {
                    grid-column-end: 4;
                  }
                }
              }

              &--2-3 {
                .grid-container__item {
                  &:nth-child(5n - 4),
                  &:nth-child(5n - 3),
                  &:nth-child(5n) {
                    grid-column: span 4;
                  }
                }
              }
            }
          }

          @media (${mq.desktop}) {
            .grid-container {
              padding: 6.25rem 0;

              &--1-1,
              &--2-2,
              &--2-3,
              &--3-3 {
                .grid-container__body {
                  grid-template-columns: repeat(6, 1fr);
                }
              }

              &--1-1 {
                .grid-container__item {
                  grid-column: 3/5;
                }
              }

              &--2-2 {
                .grid-container__item {
                  &:nth-child(odd) {
                    grid-column-end: 4;
                  }

                  &:nth-child(even) {
                    grid-column-end: -2;
                  }

                  // Center single orphan in row.
                  &:last-child:nth-child(2n - 1) {
                    grid-column-end: 5;
                  }
                }
              }

              &--2-3 {
                .grid-container__item {
                  grid-column: span 2;

                  &:nth-child(5n - 4),
                  &:nth-child(5n - 3) {
                    grid-column: span 3;
                  }

                  // Center single orphan in first row.
                  &:last-child:nth-child(5n - 4) {
                    grid-column-start: 2;
                    grid-column-end: -2;
                  }

                  // Center single orphan in second row.
                  :last-child:nth-child(5n - 2) {
                    grid-column-end: 5;
                  }

                  // Center first of two orphans in second row.
                  :nth-last-child(2):nth-child(5n - 2) {
                    grid-column-end: 4;
                  }

                  // Center second of two orphans in second row.
                  :last-child:nth-child(5n - 1) {
                    grid-column-end: 6;
                  }

                  &:nth-child(5n) {
                    grid-column: span 2;
                  }
                }
              }

              &--3-3 {
                .grid-container__item {
                  &:last-child:nth-child(3n) {
                    grid-column-end: -1;
                  }

                  // Center single orphan in row.
                  &:last-child:nth-child(3n - 2) {
                    grid-column-end: 5;
                  }

                  // Center first of two orphans in row.
                  &:nth-last-child(2):nth-child(3n + 1) {
                    grid-column-end: 4;
                  }

                  // Center second of two orphans in row.
                  &:last-child:nth-child(3n - 1) {
                    grid-column-end: -2;
                  }
                }
              }
            }
          }
        `}
      </style>
    </>
  )
}
